import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import {
} from '@/shared/interfaces/erp';
import { getUrlParamsFetch, handleAxiosError } from '@/shared/utils';
import { useUiStore } from '@/store/ui';
import { Sector, SectorsPagingRes } from '@/shared/interfaces/erp/mantenimiento-operaciones';
import { isAxiosError } from 'axios';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchSectors = (params?: GetSectorsParams, enabled = true) => {
  return useQuery({
    queryKey: ['sectors', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getSectors(params);
    },
    enabled,
  });
};

export const useGetSector = (id: string) => {
  return useQuery({
    queryKey: ['sectors', id],
    queryFn: async () => {
      return getSector(id);
    },
  });
};

export const useCreateSector = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createSector,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sectors'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Sector creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateSector = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateSector,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sectors'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Sector actualizado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useRemoveSector = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  return useMutation({
    mutationFn: removeSector,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sectors'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
      setIsGlobalLoading(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetSectorsParams = Partial<Sector> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateSectorParams = Omit<Sector, 'id_sector'>;
export type UpdateSectorParams = {
  id: string;
  data: CreateSectorParams;
};

export const getSectors = (params?: GetSectorsParams) => {
  const queryParams = getUrlParamsFetch(params || {});
  return get<SectorsPagingRes>(
    `${urlBackend}api/v1/sector/?${queryParams}`,
    true
  );
};

export const getSector = async (id: string) => {
  try {
    return await get<Sector>(`${urlBackend}api/v1/sector/${id}/`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createSector = async (data: CreateSectorParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Sector>(`${urlBackend}api/v1/sector/`, data, true);
};

export const updateSector = async ({ id, data }: UpdateSectorParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Sector>(`${urlBackend}api/v1/sector/${id}/`, data, true);
};

export const removeSector = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  const res = await remove<Sector>(`${urlBackend}api/v1/sector/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};


/***
 * 
 * Importar Excel Sector con cobertura
 * 
*/
export const useFetchImportarExcelSector = (
  { enableRedirect, navigate, returnUrl }: UseMutationsParams
) => {
  const queryClient = useQueryClient();

  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;


  return useMutation({

    mutationFn: importUploadExcelSector,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['sector-create-array'] });

      if (data.code === 200)
        enableRedirect && navigate && returnUrl && navigate(returnUrl);

      setAlert(true, data.message, data.status);
    },
    onError: (error => {
      if (isAxiosError(error)) {
        const respAxiosData = error.response?.data || {};
        if (respAxiosData.code == 400)
          return setAlert(true, `Se ha detectado un código duplicado. Por favor, corrige el archivo y vuelve a subirlo.`, AlertSeverityEnum.error)
      }
      handleAxiosError(error);
    }),
    onSettled: () => {
      setIsGlobalLoading(false);
    }
  });
};

export const importUploadExcelSector = async (data: { sectores: any[] }) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true)
  const res = await post<any>(`${urlBackend}api/v1/sector-create-array/`, data, true);
  return res;
};